// API Service for TRP Sports Booking
const API_URL = process.env.REACT_APP_BACKEND_URL;

// Lightweight in-memory + sessionStorage cache for static public data (venues rarely change).
// Availability is NEVER cached here — it always hits the network live.
const _memCache = new Map();
const _CACHE_V = 'v1'; // bump to invalidate stale-shaped cached responses
const _SOFT_MS = 30000; // within this window serve pure cache (no revalidation)

async function _fetchAndStore(key, url) {
  const response = await fetch(url);
  if (!response.ok) throw new Error('Failed to load ' + key);
  const v = await response.json();
  const entry = { t: Date.now(), v };
  _memCache.set(key, entry);
  try { sessionStorage.setItem(key, JSON.stringify(entry)); } catch (_) { /* quota */ }
  return v;
}

async function cachedJson(key, url, ttlMs = 300000) {
  key = `${_CACHE_V}_${key}`;
  const now = Date.now();
  let cached = _memCache.get(key);
  if (!cached) {
    try {
      const raw = sessionStorage.getItem(key);
      if (raw) cached = JSON.parse(raw);
    } catch (_) { /* ignore */ }
  }
  if (cached && now - cached.t < ttlMs) {
    _memCache.set(key, cached);
    // stale-while-revalidate: past the soft window, refresh in the background
    // so the next view is fresh (bounds staleness to a single render).
    if (now - cached.t > _SOFT_MS) _fetchAndStore(key, url).catch(() => {});
    return cached.v;
  }
  return _fetchAndStore(key, url);
}

// Drop cached public facility data immediately (e.g. after an admin edit).
function clearPublicFacilityCache() {
  for (const k of Array.from(_memCache.keys())) {
    if (k.includes('pub_facilit')) _memCache.delete(k);
  }
  try {
    for (let i = sessionStorage.length - 1; i >= 0; i--) {
      const k = sessionStorage.key(i);
      if (k && k.includes('pub_facilit')) sessionStorage.removeItem(k);
    }
  } catch (_) { /* ignore */ }
}


class ApiService {
  constructor() {
    this.token = localStorage.getItem('token');
  }

  setToken(token) {
    this.token = token;
    if (token) {
      localStorage.setItem('token', token);
    } else {
      localStorage.removeItem('token');
    }
  }

  getToken() {
    return this.token || localStorage.getItem('token');
  }

  async request(endpoint, options = {}) {
    const url = `${API_URL}/api${endpoint}`;
    const token = this.getToken();

    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    // Add timeout support
    const timeout = options.timeout || 30000; // 30 second default timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    // Chain caller-provided signal (e.g. from BookingContext's AbortController
    // that we use to cancel superseded fetches) into our internal one so a
    // caller-side abort also aborts the underlying fetch.
    if (options.signal) {
      if (options.signal.aborted) {
        controller.abort();
      } else {
        options.signal.addEventListener('abort', () => controller.abort(), { once: true });
      }
    }

    try {
      const response = await fetch(url, {
        ...options,
        headers,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      // Handle 401 unauthorized
      if (response.status === 401) {
        // For login endpoint, show the actual error (e.g., "Invalid credentials")
        const isLoginRequest = endpoint === '/auth/login';
        if (isLoginRequest) {
          let errMsg = 'Invalid credentials';
          try {
            const errData = await response.json();
            errMsg = errData.detail || errMsg;
          } catch (parseErr) {
            // Response body was not valid JSON. Fall back to the default
            // 'Invalid credentials' message. Logged for diagnostics so we can
            // tell genuine 401s apart from malformed server responses.
            console.warn('Login 401: response body was not valid JSON.', parseErr);
          }
          throw new Error(errMsg);
        }

        // For other endpoints, clear auth and redirect
        this.setToken(null);
        localStorage.removeItem('user');
        localStorage.removeItem('facility');
        localStorage.removeItem('fcm_token');
        localStorage.removeItem('notification_prompt_shown');
        sessionStorage.clear();
        
        if (!window.location.pathname.includes('/login')) {
          window.location.href = '/admin/login';
        }
        throw new Error('Session expired. Please login again.');
      }

      // Try to parse JSON response
      let data;
      const contentType = response.headers.get('content-type');
      
      // Get the response text first
      let responseText = '';
      try {
        responseText = await response.text();
      } catch (textError) {
        console.error('Error reading response text:', textError);
        throw new Error('Network error: Connection interrupted. Please check your internet and try again.');
      }
      
      if (contentType && contentType.includes('application/json') && responseText) {
        try {
          data = JSON.parse(responseText);
        } catch (parseError) {
          console.error('JSON parse error:', parseError, 'Response:', responseText.substring(0, 200));
          // Return meaningful error based on response status
          if (!response.ok) {
            throw new Error(`Server error (${response.status}): ${responseText.substring(0, 100) || 'Unknown error'}`);
          }
          throw new Error(`Unable to parse server response: ${responseText.substring(0, 50)}`);
        }
      } else if (responseText) {
        data = { detail: responseText };
      } else {
        data = { detail: 'Empty server response' };
      }

      if (!response.ok) {
        // Normalise `detail` — FastAPI can return it as a string OR an object
        // (e.g. QR creation now returns the full Razorpay error object). Render
        // objects into a readable message instead of "[object Object]".
        let detailMsg = data.detail;
        if (detailMsg && typeof detailMsg === 'object') {
          const rzp = detailMsg.razorpay_error;
          if (rzp && typeof rzp === 'object') {
            const isSet = (v) => v && v !== 'NA';
            const parts = [rzp.description || rzp.reason || 'Razorpay error'];
            if (isSet(rzp.code)) parts.push(`[${rzp.code}]`);
            if (isSet(rzp.reason) && rzp.reason !== rzp.description) parts.push(`reason: ${rzp.reason}`);
            if (isSet(rzp.step)) parts.push(`step: ${rzp.step}`);
            if (detailMsg.http_status) parts.push(`(HTTP ${detailMsg.http_status})`);
            detailMsg = parts.join(' ');
          } else {
            detailMsg = detailMsg.message || JSON.stringify(detailMsg);
          }
        }

        // Handle specific HTTP status codes
        if (response.status === 409) {
          throw new Error(detailMsg || 'Slot is not available - conflicts with existing booking');
        }
        if (response.status === 500) {
          // Surface the backend's specific detail when present (e.g. the real
          // Razorpay error for QR creation) instead of a generic message.
          throw new Error(detailMsg || 'Server error. Please try again or contact support.');
        }
        if (response.status === 504 || response.status === 408) {
          throw new Error('Request timed out. Please try again.');
        }
        throw new Error(detailMsg || `Request failed with status ${response.status}`);
      }

      return data;
    } catch (error) {
      clearTimeout(timeoutId);
      
      // Handle abort/timeout errors
      if (error.name === 'AbortError') {
        throw new Error('Request timed out. Please check your connection and try again.');
      }
      
      // Handle network errors
      if (error.message === 'Failed to fetch') {
        throw new Error('Network error. Please check your internet connection.');
      }
      
      console.error('API Error:', error);
      throw error;
    }
  }

  // Auth endpoints
  async login(email, password) {
    // Clear any existing auth data before login attempt
    this.setToken(null);
    localStorage.removeItem('user');
    localStorage.removeItem('facility');
    
    try {
      const data = await this.request('/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      });
      
      if (data.access_token) {
        this.setToken(data.access_token);
        return data;
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (error) {
      // Ensure token is cleared on login failure
      this.setToken(null);
      throw error;
    }
  }

  async getCurrentUser() {
    return this.request('/auth/me');
  }

  async changePassword(currentPassword, newPassword) {
    return this.request('/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
    });
  }

  logout() {
    this.setToken(null);
  }

  // Bookings endpoints
  async getBookings(params = {}, options = {}) {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString ? `/bookings?${queryString}` : '/bookings';
    return this.request(endpoint, options);
  }

  async getBooking(id) {
    return this.request(`/bookings/${id}`);
  }

  async createBooking(bookingData) {
    return this.request('/bookings', {
      method: 'POST',
      body: JSON.stringify(bookingData),
    });
  }

  async updateBooking(id, updates) {
    return this.request(`/bookings/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async cancelBooking(id, sendEmail = true, refundPercent = 0) {
    const params = new URLSearchParams({
      send_email: String(sendEmail),
      refund_percent: String(refundPercent || 0),
    });
    return this.request(`/bookings/${id}?${params.toString()}`, {
      method: 'DELETE',
    });
  }

  async collectPayment(id, paymentData) {
    return this.request(`/bookings/${id}/collect-payment`, {
      method: 'POST',
      body: JSON.stringify(paymentData),
    });
  }

  // ----- Rental / add-on catalogue + booking lines -----
  async getAddonCatalogue(venueId, activeOnly = true) {
    const params = new URLSearchParams();
    if (venueId) params.set('venue_id', venueId);
    if (activeOnly) params.set('active_only', 'true');
    return this.request(`/addons/catalogue?${params.toString()}`);
  }
  async createAddonItem(data) {
    return this.request('/addons/catalogue', { method: 'POST', body: JSON.stringify(data) });
  }
  async updateAddonItem(itemId, data) {
    return this.request(`/addons/catalogue/${itemId}`, { method: 'PATCH', body: JSON.stringify(data) });
  }
  async deactivateAddonItem(itemId) {
    return this.request(`/addons/catalogue/${itemId}`, { method: 'DELETE' });
  }
  async addBookingAddon(bookingId, itemId, quantity) {
    return this.request(`/bookings/${bookingId}/addons`, {
      method: 'POST', body: JSON.stringify({ item_id: itemId, quantity }),
    });
  }
  async editBookingAddon(bookingId, lineId, quantity) {
    return this.request(`/bookings/${bookingId}/addons/${lineId}`, {
      method: 'PATCH', body: JSON.stringify({ quantity }),
    });
  }
  async removeBookingAddon(bookingId, lineId, force = false) {
    return this.request(`/bookings/${bookingId}/addons/${lineId}?force=${force}`, { method: 'DELETE' });
  }
  async collectBookingAddon(bookingId, lineId, method) {
    return this.request(`/bookings/${bookingId}/addons/${lineId}/collect`, {
      method: 'POST', body: JSON.stringify({ method }),
    });
  }

  // Customers endpoints
  async getCustomers(search = '') {
    const endpoint = search ? `/customers?search=${encodeURIComponent(search)}` : '/customers';
    return this.request(endpoint);
  }

  async lookupCustomer(phone) {
    return this.request(`/customers/lookup?phone=${encodeURIComponent(phone)}`);
  }

  async getCustomer(id) {
    return this.request(`/customers/${id}`);
  }

  async createCustomer(customerData) {
    return this.request('/customers', {
      method: 'POST',
      body: JSON.stringify(customerData),
    });
  }

  async updateCustomer(id, updates) {
    return this.request(`/customers/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  // Blocked slots endpoints
  async getBlockedSlots(date = '') {
    const endpoint = date ? `/blocked-slots?date=${date}` : '/blocked-slots';
    return this.request(endpoint);
  }

  async createBlockedSlot(slotData) {
    return this.request('/blocked-slots', {
      method: 'POST',
      body: JSON.stringify(slotData),
    });
  }

  async deleteBlockedSlot(id) {
    return this.request(`/blocked-slots/${id}`, {
      method: 'DELETE',
    });
  }

  // Settings endpoints
  async getSettings() {
    return this.request('/settings');
  }

  async getPublicSettings() {
    // Public endpoint - no auth required
    const url = `${API_URL}/api/settings/public`;
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error('Failed to load settings');
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching public settings:', error);
      throw error;
    }
  }

  async getPublicTimeSlots() {
    // Public endpoint - no auth required  
    const url = `${API_URL}/api/slots/time-slots`;
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error('Failed to load time slots');
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching time slots:', error);
      throw error;
    }
  }

  async createPublicBooking(bookingData) {
    // Public booking endpoint - no auth required
    const url = `${API_URL}/api/bookings/public`;
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(bookingData),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to create booking');
      }
      
      return data;
    } catch (error) {
      console.error('Error creating public booking:', error);
      throw error;
    }
  }

  async updateSettings(updates) {
    return this.request('/settings', {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  // Slots endpoints
  async getTimeSlots() {
    return this.request('/slots/time-slots');
  }

  // Admin endpoint - requires auth, returns slots as array
  async getSlotAvailability(date, facilityId = null) {
    let endpoint = `/slots/availability?date=${date}`;
    if (facilityId) {
      endpoint += `&facility_id=${facilityId}`;
    }
    return this.request(endpoint);
  }

  // SEC-005: read-only venue roles use the sanitized occupancy grid (no PII / amounts).
  async getCalendarOccupancy(date) {
    return this.request(`/calendar/occupancy?date=${date}`);
  }

  // Public endpoint - no auth required, returns slots as object keyed by time
  async getPublicSlotAvailability(date, facilityId = null) {
    let url = `${API_URL}/api/slots/public-availability?date=${date}`;
    if (facilityId) {
      url += `&facility_id=${facilityId}`;
    }
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error('Failed to fetch availability');
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching public availability:', error);
      throw error;
    }
  }

  async createCustomerBooking(bookingData) {
    // Public booking endpoint - uses customer token if available
    const url = `${API_URL}/api/bookings/public`;
    const customerToken = localStorage.getItem('customer_token');
    
    const headers = {
      'Content-Type': 'application/json',
    };
    
    if (customerToken) {
      headers['Authorization'] = `Bearer ${customerToken}`;
    }
    
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(bookingData),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to create booking');
      }
      
      return data;
    } catch (error) {
      console.error('Error creating customer booking:', error);
      throw error;
    }
  }

  async calculatePrice(params) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/slots/price?${queryString}`);
  }

  // Payment endpoints
  async createPaymentOrder(bookingId, amount) {
    return this.request('/payments/create-order', {
      method: 'POST',
      body: JSON.stringify({ booking_id: bookingId, amount }),
    });
  }

  async createPublicPaymentOrder(bookingId, amount, paymentMode = 'full') {
    // Public payment endpoint - no auth required
    const url = `${API_URL}/api/payments/create-order-public`;
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ booking_id: bookingId, amount, payment_mode: paymentMode }),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to create payment order');
      }
      
      return data;
    } catch (error) {
      console.error('Error creating payment order:', error);
      throw error;
    }
  }

  async verifyPayment(paymentData) {
    return this.request('/payments/verify', {
      method: 'POST',
      body: JSON.stringify(paymentData),
    });
  }

  async verifyPublicPayment(paymentData) {
    // Public payment verification - no auth required
    const url = `${API_URL}/api/payments/verify-public`;
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(paymentData),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Payment verification failed');
      }
      
      return data;
    } catch (error) {
      console.error('Error verifying payment:', error);
      throw error;
    }
  }

  async createPaymentLink(paymentLinkData) {
    return this.request('/payments/create-payment-link', {
      method: 'POST',
      body: JSON.stringify(paymentLinkData),
    });
  }

  async getPaymentHistory(bookingId) {
    return this.request(`/payments/history/${bookingId}`);
  }

  async backfillRazorpayRefs(payload) {
    return this.request('/payments/backfill-razorpay-refs', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  // WhatsApp endpoints
  async sendWhatsAppMessage(phoneNumber, message) {
    return this.request('/whatsapp/send-message', {
      method: 'POST',
      body: JSON.stringify({ phone_number: phoneNumber, message }),
    });
  }

  async sendBookingConfirmation(data) {
    return this.request('/whatsapp/send-booking-confirmation', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async sendBookingReminder(data) {
    return this.request('/whatsapp/send-reminder', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async sendPaymentConfirmation(data) {
    return this.request('/whatsapp/send-payment-confirmation', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async sendPaymentLink(data) {
    return this.request('/whatsapp/send-payment-link', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getWhatsAppTemplates() {
    return this.request('/whatsapp/templates');
  }

  async getWhatsAppLogs(phone = null, bookingId = null) {
    let url = '/whatsapp/message-logs?';
    if (phone) url += `phone=${phone}&`;
    if (bookingId) url += `booking_id=${bookingId}&`;
    return this.request(url);
  }

  // Facility endpoints
  async getPublicFacilities() {
    try {
      return await cachedJson('pub_facilities', `${API_URL}/api/facilities/public`);
    } catch (error) {
      console.error('Error fetching facilities:', error);
      throw error;
    }
  }

  async getPublicFacility(slug) {
    try {
      return await cachedJson(`pub_facility_${slug}`, `${API_URL}/api/facilities/public/${slug}`);
    } catch (error) {
      console.error('Error fetching facility:', error);
      throw error;
    }
  }

  async getFacilities() {
    return this.request('/facilities');
  }

  async getFacility(id) {
    return this.request(`/facilities/${id}`);
  }

  async updateFacility(id, updates) {
    const res = await this.request(`/facilities/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
    clearPublicFacilityCache(); // admin edit → public pages fetch fresh immediately
    return res;
  }

  async getFacilityIntegrations(id) {
    return this.request(`/facilities/${id}/integrations`);
  }

  async updateFacilityIntegration(facilityId, integrationType, config) {
    return this.request(`/facilities/${facilityId}/integrations/${integrationType}`, {
      method: 'PUT',
      body: JSON.stringify(config),
    });
  }

  // Get facility-specific slot availability
  async getFacilitySlotAvailability(facilityId, date) {
    return this.request(`/slots/availability?date=${date}&facility_id=${facilityId}`);
  }

  // Get bookings for a customer (by phone or email)
  async getCustomerBookings(identifier) {
    const url = `${API_URL}/api/bookings/customer?identifier=${encodeURIComponent(identifier)}`;
    const firebaseToken = localStorage.getItem('firebase_token');
    
    const headers = {};
    if (firebaseToken) {
      headers['Authorization'] = `Bearer ${firebaseToken}`;
    }
    
    try {
      const response = await fetch(url, { headers });
      if (!response.ok) {
        throw new Error('Failed to load bookings');
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching customer bookings:', error);
      return [];
    }
  }

  // ============== Coupon Endpoints ==============
  
  // Get all coupons (Super Admin only)
  async getCoupons(includeInactive = false) {
    return this.request(`/coupons?include_inactive=${includeInactive}`);
  }

  // Public promo coupons shown on the booking page (no auth)
  async getPublicCoupons(facilityId) {
    const url = `${API_URL}/api/coupons/public?facility_id=${encodeURIComponent(facilityId)}`;
    try {
      const res = await fetch(url);
      if (!res.ok) return [];
      return await res.json();
    } catch (e) { return []; }
  }

  // Get single coupon (Super Admin only)
  async getCoupon(couponId) {
    return this.request(`/coupons/${couponId}`);
  }

  // Create coupon (Super Admin only)
  async createCoupon(couponData) {
    return this.request('/coupons', {
      method: 'POST',
      body: JSON.stringify(couponData),
    });
  }

  // Update coupon (Super Admin only)
  async updateCoupon(couponId, updates) {
    return this.request(`/coupons/${couponId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  // Delete (deactivate) coupon (Super Admin only)
  async deleteCoupon(couponId) {
    return this.request(`/coupons/${couponId}`, {
      method: 'DELETE',
    });
  }

  // Activate coupon (Super Admin only)
  async activateCoupon(couponId) {
    return this.request(`/coupons/${couponId}/activate`, {
      method: 'POST',
    });
  }

  // Validate coupon code (Public endpoint)
  async validateCoupon(code, facilityId, bookingAmount) {
    const url = `${API_URL}/api/coupons/validate`;
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          code,
          facility_id: facilityId,
          booking_amount: bookingAmount,
        }),
      });
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error validating coupon:', error);
      return { valid: false, message: 'Unable to validate coupon' };
    }
  }

  // ============== Bulk Email Endpoint ==============
  
  // Send bulk booking summary email
  async sendBulkBookingSummaryEmail(data) {
    return this.request('/bookings/bulk-email-summary', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Send consolidated bulk booking push notification
  async sendBulkBookingPushNotification(data) {
    return this.request('/bookings/bulk-push-notification', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // ============== Customer Search Endpoint ==============
  
  // Search customers by name (for autocomplete)
  async searchCustomersByName(query) {
    return this.request(`/customers/search?query=${encodeURIComponent(query)}`);
  }

  // ============== Push Notifications (FCM) ==============
  
  // Save FCM token for current user
  async saveFcmToken(token) {
    return this.request('/notifications/fcm-token', {
      method: 'POST',
      body: JSON.stringify({ token }),
    });
  }

  // Remove FCM token (unsubscribe)
  async removeFcmToken(token) {
    return this.request('/notifications/fcm-token', {
      method: 'DELETE',
      body: JSON.stringify({ token }),
    });
  }

  // ──── Operations Task Management ────
  async getTaskTemplates(facilityId) {
    const qs = facilityId ? `?facility_id=${facilityId}` : '';
    return this.request(`/operations/templates${qs}`);
  }
  async createTaskTemplate(data) {
    return this.request('/operations/templates', { method: 'POST', body: JSON.stringify(data) });
  }
  async updateTaskTemplate(id, data) {
    return this.request(`/operations/templates/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  }
  async deleteTaskTemplate(id) {
    return this.request(`/operations/templates/${id}`, { method: 'DELETE' });
  }
  async generateDailyTasks(date) {
    return this.request(`/operations/generate-daily?date=${date}`, { method: 'POST' });
  }
  async getTasks(params = {}) {
    const qs = new URLSearchParams(params).toString();
    return this.request(`/operations/tasks?${qs}`);
  }
  async completeTask(taskId) {
    return this.request(`/operations/tasks/${taskId}/complete`, { method: 'PUT' });
  }
  async uploadTaskImage(taskId, file) {
    const formData = new FormData();
    formData.append('file', file);
    const token = localStorage.getItem('auth_token');
    const res = await fetch(`${this.baseURL}/operations/tasks/${taskId}/upload-image`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
      body: formData
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'Upload failed');
    }
    return res.json();
  }
  async getTaskStats(params = {}) {
    const qs = new URLSearchParams(params).toString();
    return this.request(`/operations/stats?${qs}`);
  }
  async markMissedTasks(date) {
    return this.request(`/operations/mark-missed?date=${date}`, { method: 'POST' });
  }
  async getOperationsReport(params = {}) {
    const qs = new URLSearchParams(params).toString();
    return this.request(`/operations/reports/summary?${qs}`);
  }
  async exportOperationsCSV(params = {}) {
    const qs = new URLSearchParams(params).toString();
    const token = localStorage.getItem('auth_token');
    const res = await fetch(`${this.baseURL}/operations/reports/export?${qs}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!res.ok) throw new Error('Export failed');
    return res.blob();
  }
}

// Export singleton instance
export const api = new ApiService();
export default api;
